import { fromZonedTime } from 'date-fns-tz'
import { z } from 'zod'
import { ajouterJours, jourAParis } from './calendrier'
import {
  CADENCES_CONNEXION,
  FUSEAU,
  type DeclencheurCollecte,
  type EtatConnexionPortail,
  type ModeTransmission,
  type StatutCollecte,
  type StatutDefi,
  type TypeDefi,
} from './domaine'

// Collecte web : règles pures du collecteur de documents sur les portails des fournisseurs.
// Le collecteur est un navigateur piloté par des adaptateurs écrits dans le code, sans modèle :
// une page externe est hostile, son texte n'est jamais une consigne. Ce qui n'est pas déclaré
// par l'adaptateur (domaine, écriture, capacité) est interdit. Aucune capacité N2 n'existe.

// Fournisseurs connus : un adaptateur écrit dans le collecteur (apps/collecteur) par clé.
// « essai » : portail factice, refusé en production.

// `robot` faux : pas encore d'adaptateur (visite supervisée à faire, docs/visite-portail.md) ;
// la connexion sert au dossier de dépôt de l'ordinateur, aucune collecte n'est mise en file.
// `sensible` : compte dont l'identifiant n'est jamais confié (CLAUDE.md, « Collecte locale sur un
// compte sensible ») : jamais de secret, jamais le collecteur du serveur ni la vue en direct ;
// seul l'ordinateur du titulaire le relève, une fois que celui-ci s'y est connecté lui-même.
// `payeur` : portail d'achats payés par carte ; chaque facture ouvre la décision « qui a payé, à
// qui refacturer » (packages/core/src/cartes.ts).
// `justificatifs` : portail de justificatifs du cabinet (CLAUDE.md, « Dépôt et affectation des
// justificatifs chez le cabinet ») ; on y lit les opérations bancaires pour les rapprocher des
// pièces (packages/core/src/justificatifs-portail.ts), sans y relever de documents. `mode` est la
// valeur de `entites.transmission` d'une entité dont les pièces y sont déposées ;
// `categorieDepotExigee` : le dépôt s'y fait dans une catégorie de documents lue au passage (Tiime),
// sans laquelle rien ne se dépose ; absente, aucune catégorie n'est attendue (Dougs). `appli` :
// l'adresse de son appli web, où un humain va voir une opération (« Voir dans Tiime »).

/** Mode d'une entité dont les pièces se déposent sur un portail : ni « aucune », ni « mail ». */
export type ModeDepot = Exclude<ModeTransmission, 'aucune' | 'mail'>

type FichePortail = {
  libelle: string
  essai: boolean
  robot: boolean
  sensible: boolean
  payeur?: boolean
  /** Place de marché : l'émetteur d'une facture est le vendeur, la plateforme est payée. */
  placeDeMarche?: boolean
  justificatifs?: { mode: ModeDepot; categorieDepotExigee?: true; appli: string }
}

export const FOURNISSEURS_PORTAIL = {
  factice: { libelle: 'Portail factice (essais)', essai: true, robot: true, sensible: false },
  bouygues: {
    libelle: 'Bouygues Telecom (espace client)',
    essai: false,
    robot: true,
    sensible: false,
  },
  uber: { libelle: 'Uber (espace passager)', essai: false, robot: true, sensible: false },
  eaux_de_vienne: {
    libelle: 'Eaux de Vienne (agence en ligne)',
    essai: false,
    robot: true,
    sensible: false,
  },
  edf: { libelle: 'EDF (espace client)', essai: false, robot: true, sensible: false },
  aon: { libelle: 'Aon (espace adhérent, mutuelle)', essai: false, robot: true, sensible: false },
  ameli: { libelle: 'Ameli (compte assuré)', essai: false, robot: true, sensible: true },
  // Une banque : on y vire. Le titulaire se connecte lui-même, le robot consulte et télécharge.
  credit_agricole: {
    libelle: 'Crédit Agricole (documents, compte particulier)',
    essai: false,
    robot: true,
    sensible: true,
  },
  credit_agricole_pro: {
    libelle: 'Crédit Agricole (documents, compte de société)',
    essai: false,
    robot: true,
    sensible: true,
  },
  google_ads: {
    libelle: 'Google Ads (factures, paiements Google)',
    essai: false,
    robot: true,
    sensible: true,
  },
  // On y achète sans validation forte (commande en un clic) : compte sensible.
  amazon: {
    libelle: 'Amazon (commandes, factures)',
    essai: false,
    robot: true,
    sensible: true,
    payeur: true,
    placeDeMarche: true,
  },
  // On y commande des badges et change de formule ou d'IBAN sans validation forte : sensible.
  ulys: {
    libelle: 'Ulys (télépéage VINCI Autoroutes)',
    essai: false,
    robot: true,
    sensible: true,
  },
  // On y règle une facture et change le mode de paiement : sensible, relevé par l'ordinateur du
  // titulaire seulement (visite du 14/10/2026).
  engie_pro: {
    libelle: 'ENGIE Professionnels (gaz, électricité)',
    essai: false,
    robot: true,
    sensible: true,
  },
  // Abonnements payés par la carte enregistrée, où l'on souscrit ou change de formule en ligne :
  // comptes sensibles. Factures hébergées par Stripe (ChatGPT) ; historique de facturation (Adobe).
  chatgpt: {
    libelle: 'ChatGPT (OpenAI, factures)',
    essai: false,
    robot: true,
    sensible: true,
  },
  adobe: {
    libelle: 'Adobe (Creative Cloud, factures)',
    essai: false,
    robot: true,
    sensible: true,
  },
  // On y commande un taxi réglé par la carte enregistrée, sans validation forte : sensible, relevé
  // par l'ordinateur du titulaire seulement (visite du 30/10/2026).
  g7: {
    libelle: 'G7 (taxis, justificatifs de course)',
    essai: false,
    robot: true,
    sensible: true,
  },
  // Fournisseur d'identité, achats sur la carte enregistrée : sensible, relevé par l'ordinateur du
  // titulaire seulement (visite du 30/10/2026). Payé par des cartes de plusieurs entités.
  apple: {
    libelle: 'Apple (compte, factures)',
    essai: false,
    robot: true,
    sensible: true,
    payeur: true,
  },
  // Banque et dossier comptable de sociétés : compte sensible, lu par l'agent du Mac du titulaire
  // dans l'onglet prêté de son Chrome, jamais par le serveur.
  tiime: {
    libelle: 'Tiime (banque, justificatifs)',
    essai: false,
    robot: true,
    sensible: true,
    justificatifs: { mode: 'tiime', categorieDepotExigee: true, appli: 'https://apps.tiime.fr' },
  },
  // Dossier comptable de SC PMVB : compte sensible, lu de même par l'agent du Mac du titulaire,
  // dans l'onglet prêté de son Chrome (décision du 12/10/2026, étape 1 : lecture et passage à blanc).
  dougs: {
    libelle: 'Dougs (comptabilité, justificatifs)',
    essai: false,
    robot: true,
    sensible: true,
    justificatifs: { mode: 'dougs', appli: 'https://app.dougs.fr' },
  },
} as const satisfies Record<string, FichePortail>
export type FournisseurPortail = keyof typeof FOURNISSEURS_PORTAIL

/** La fiche d'un fournisseur connu ; null pour un inconnu (jamais une clé héritée). */
function fiche(fournisseur: string): FichePortail | null {
  return Object.hasOwn(FOURNISSEURS_PORTAIL, fournisseur)
    ? FOURNISSEURS_PORTAIL[fournisseur as FournisseurPortail]
    : null
}

/** Un adaptateur relève ce fournisseur ; sinon, seuls les dépôts à la main entrent. */
export function collecteParRobot(fournisseur: string): boolean {
  return fiche(fournisseur)?.robot ?? false
}

/**
 * Mode de dépôt d'un portail de justificatifs : la valeur de `entites.transmission` des entités
 * dont il reçoit les pièces ; null pour un portail de documents ou un fournisseur inconnu.
 */
export function modeDeDepot(fournisseur: string): ModeDepot | null {
  return fiche(fournisseur)?.justificatifs?.mode ?? null
}

/**
 * Le dépôt chez ce portail de justificatifs exige une catégorie de documents lue au passage
 * (Tiime) : sans elle, rien ne se dépose et le bilan le dit. Faux pour un portail qui n'en a pas
 * (Dougs), un portail de documents ou un fournisseur inconnu.
 */
export function categorieDepotExigee(fournisseur: string): boolean {
  return fiche(fournisseur)?.justificatifs?.categorieDepotExigee === true
}

/** Portails de justificatifs : on y lit des opérations, jamais de collecte planifiée de documents. */
export const FOURNISSEURS_JUSTIFICATIFS = Object.keys(FOURNISSEURS_PORTAIL).filter(
  (cle) => modeDeDepot(cle) !== null,
)

/**
 * Le collecteur y relève des documents (factures, avis) : un adaptateur, et pas un portail de
 * justificatifs. « Récupérer la pièce » ne propose que ceux-là.
 */
export function collecteDeDocuments(fournisseur: string): boolean {
  return collecteParRobot(fournisseur) && modeDeDepot(fournisseur) === null
}

/** L'appli web d'un portail de justificatifs (« Voir dans Tiime ») ; null pour un autre portail. */
export function appliDuPortail(fournisseur: string): string | null {
  return fiche(fournisseur)?.justificatifs?.appli ?? null
}

// « Récupérer via <connecteur> » dans le bilan des justificatifs : le collecteur de documents que
// désigne le marchand ou le libellé d'une opération lue chez le cabinet, sans modèle. Des mots par
// fournisseur, cherchés en début de mot dans le texte sans accents ni casse (OPENAI → ChatGPT,
// AUTOROUTES ou VINCI → Ulys). Une proposition seulement : un humain lance la collecte, aux règles
// de la collecte à la demande.

/** Les mots qui désignent chaque fournisseur relevé par un collecteur de documents. */
export const MOTS_CONNECTEUR: Readonly<Partial<Record<FournisseurPortail, readonly string[]>>> = {
  chatgpt: ['openai', 'chatgpt'],
  // VINCI Autoroutes et ses réseaux (ASF, Cofiroute, Escota) facturent le télépéage Ulys.
  ulys: ['ulys', 'vinci', 'autoroute', 'asf', 'cofiroute', 'escota'],
  adobe: ['adobe'],
  // Les débits du compte Apple, pas « APPLE PAY » (un autre marchand) ni « APPLE STORE » (un magasin).
  apple: ['apple com bill', 'apple cork'],
  amazon: ['amazon', 'amzn'],
  edf: ['edf'],
  // Relevé le 14/10/2026 : en production, les seuls débits « ENGIE » sont ceux du contrat pro des
  // communs (SC PMVB) ; un contrat particulier ENGIE n'aurait pas ses factures dans cet espace.
  engie_pro: ['engie'],
  bouygues: ['bouygues', 'bytel'],
  // Seulement la publicité : les autres débits Google (Workspace, One, Cloud) ne sont pas dans les
  // factures que relève l'adaptateur Google Ads.
  google_ads: ['google ads', 'googleads', 'adwords'],
  uber: ['uber'],
  g7: ['g7'],
  eaux_de_vienne: ['eaux de vienne'],
  aon: ['aon'],
}

/** Le texte en mots : sans accents, minuscules, ponctuation remplacée par des espaces. */
const enMots = (t: string) =>
  ` ${t
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()} `

/**
 * Le fournisseur qu'un marchand ou un libellé bancaire désigne : le premier de `MOTS_CONNECTEUR`
 * dont un mot ouvre un mot du texte (« AUTOROUTES » pour « autoroute ») ; null sans correspondance.
 */
export function fournisseurDuLibelle(texte: string): FournisseurPortail | null {
  const mots = enMots(texte)
  for (const [f, liste] of Object.entries(MOTS_CONNECTEUR) as [
    FournisseurPortail,
    readonly string[],
  ][])
    if (liste.some((m) => mots.includes(` ${m}`))) return f
  return null
}

/**
 * La connexion proposée pour récupérer la pièce d'une opération : un collecteur de documents du
 * fournisseur que désignent son marchand et son libellé, d'abord une connexion active de l'entité
 * de l'opération, puis une active d'une autre entité, enfin une désactivée ; null sans
 * correspondance, ou sans connexion de ce fournisseur.
 */
export function connexionProposee<
  C extends { fournisseur: string; entiteId: string | null; active: boolean },
>(
  op: { marchand: string | null; libelle: string },
  connexions: readonly C[],
  entiteId: string | null,
): C | null {
  const f = fournisseurDuLibelle(`${op.marchand ?? ''} ${op.libelle}`)
  if (!f || !collecteDeDocuments(f)) return null
  const rang = (c: C) => (c.entiteId === entiteId ? 0 : 1) + (c.active ? 0 : 2)
  return (
    connexions
      .filter((c) => c.fournisseur === f)
      .map((c, i) => ({ c, i }))
      .sort((a, b) => rang(a.c) - rang(b.c) || a.i - b.i)[0]?.c ?? null
  )
}

export const FOURNISSEURS_SANS_ROBOT = Object.entries(FOURNISSEURS_PORTAIL)
  .filter(([, f]) => !f.robot)
  .map(([cle]) => cle)
/** Compte sensible ; un fournisseur inconnu est tenu pour sensible (refusé de toute façon). */
/** Place de marché : l'émetteur d'une facture y est le vendeur, la plateforme est payée. */
export function estPlaceDeMarche(fournisseur: string): boolean {
  if (!Object.hasOwn(FOURNISSEURS_PORTAIL, fournisseur)) return false
  const f = FOURNISSEURS_PORTAIL[fournisseur as keyof typeof FOURNISSEURS_PORTAIL]
  return 'placeDeMarche' in f && f.placeDeMarche
}

export function compteSensible(fournisseur: string): boolean {
  return Object.hasOwn(FOURNISSEURS_PORTAIL, fournisseur)
    ? FOURNISSEURS_PORTAIL[fournisseur as FournisseurPortail].sensible
    : true
}

export const FOURNISSEURS_SENSIBLES = Object.entries(FOURNISSEURS_PORTAIL)
  .filter(([, f]) => f.sensible)
  .map(([cle]) => cle)
const CLES_FOURNISSEURS = Object.keys(FOURNISSEURS_PORTAIL) as [
  FournisseurPortail,
  ...FournisseurPortail[],
]

/**
 * Nom du secret dans Bitwarden, devenu variable d'environnement du collecteur (`bws run`). Le
 * préfixe borne ce que le collecteur retire de son environnement au démarrage.
 */
export const REFERENCE_SECRET = /^PORTAIL_[A-Z0-9_]{2,56}$/

export const connexionPortailSaisie = z
  .object({
    entiteId: z.uuid().nullable(),
    fournisseur: z.enum(CLES_FOURNISSEURS),
    libelle: z.string().trim().min(1).max(120),
    tiersId: z.uuid().nullable().default(null),
    referenceSecret: z
      .string()
      .trim()
      .regex(
        REFERENCE_SECRET,
        'Nom de secret : PORTAIL_ puis majuscules, chiffres et _, comme PORTAIL_EDF_SCI',
      )
      .nullable(),
    titulaireId: z.uuid(),
    frequenceJours: z.int().min(1).max(31).default(7),
    cadence: z.enum(CADENCES_CONNEXION).default('inconnue'),
  })
  .strict()
  .refine((d) => d.referenceSecret === null || !compteSensible(d.fournisseur), {
    message: 'Compte sensible : aucun identifiant n’est confié, le titulaire se connecte lui-même.',
    path: ['referenceSecret'],
  })
export type ConnexionPortailSaisie = z.infer<typeof connexionPortailSaisie>

/**
 * Créer une connexion via l'assistant (MCP, décision de Pierre-Marie du 08/10/2026) : sans secret
 * seulement (compte sensible, ou portail relevé sans identifiant) ; une connexion qui désigne un
 * secret du coffre se crée dans l'appli. Modifier, supprimer : dans l'appli.
 */
export function erreurCreationViaAssistant(d: { referenceSecret: string | null }): string | null {
  return d.referenceSecret === null
    ? null
    : 'Une connexion avec un secret du coffre se crée dans l’appli, jamais via l’assistant.'
}

export const connexionPortailModification = z
  .object({
    libelle: z.string().trim().min(1).max(120),
    tiersId: z.uuid().nullable(),
    referenceSecret: z.string().trim().regex(REFERENCE_SECRET).nullable(),
    titulaireId: z.uuid(),
    frequenceJours: z.int().min(1).max(31),
    cadence: z.enum(CADENCES_CONNEXION),
    active: z.boolean(),
  })
  .partial()
  .strict()
export type ConnexionPortailModification = z.infer<typeof connexionPortailModification>

/** Code saisi par un humain ; vérifié ensuite par `codeDefiValide`. */
export const reponseDefi = z.object({ code: z.string().min(1).max(40) }).strict()

// Capacités

/** Ce qu'un adaptateur peut déclarer : se connecter, consulter, télécharger (N0). */
export const CAPACITES_PERMISES = ['authentifier', 'consulter', 'telecharger'] as const
export type CapacitePermise = (typeof CAPACITES_PERMISES)[number]

/**
 * Ce qu'un identifiant peut permettre chez le fournisseur sans que le collecteur ait le droit
 * de le faire : gestes N2 (payer, signer, déclarer, engager), et ce qui change le compte.
 * Déclarées à `false`, toujours ; le type ne permet pas d'autre valeur.
 */
export const CAPACITES_INTERDITES = [
  'payer',
  'modifier_banque',
  'modifier_contrat',
  'souscrire',
  'resilier',
  'declarer',
  'signer',
  'modifier_identifiants',
  'envoyer_message',
] as const
export type CapaciteInterdite = (typeof CAPACITES_INTERDITES)[number]

/**
 * Écritures métier d'un portail de justificatifs (CLAUDE.md, « Dépôt et affectation des
 * justificatifs chez le cabinet ») : déposer le justificatif d'une pièce de l'appli, le relier à
 * l'opération qu'il justifie. Facultatives dans `Capacites` : seul un fournisseur dont la table
 * `ECRITURES_METIER` les porte peut les déclarer vraies.
 */
export const CAPACITES_ECRITURE = ['deposer_justificatif', 'relier_justificatif'] as const
export type CapaciteEcriture = (typeof CAPACITES_ECRITURE)[number]

export type Capacites = { readonly [K in CapacitePermise]: boolean } & {
  readonly [K in CapaciteInterdite]: false
} & { readonly [K in CapaciteEcriture]?: boolean }

/**
 * Requête d'écriture permise : méthode et chemin exact, ou préfixe terminé par « /* », et l'hôte
 * exact où elle part (`domaine`, parmi les domaines déclarés) : sur un autre domaine déclaré, le
 * même chemin est refusé. Sans `domaine`, tout domaine déclaré (à éviter dès qu'il y en a plus
 * d'un).
 */
export type EcriturePermise = {
  readonly methode: string
  readonly chemin: string
  readonly domaine?: string
}

/**
 * Les écritures génériques rattachées à un autre hôte : la copie fictive d'un portail, servie sur
 * la boucle locale (essais seulement ; en production, la boucle locale n'est jamais déclarée).
 */
export function ecrituresVers(
  ecritures: readonly EcriturePermise[],
  hote: string,
): EcriturePermise[] {
  return ecritures.map((e) => (e.domaine === undefined ? e : { ...e, domaine: hote }))
}

// Écritures métier : déclarées en données par fournisseur, jamais permises par la seule déclaration

/** Segment d'un chemin d'écriture métier qui reçoit un identifiant : 1 à 15 chiffres. */
export const NUMERO = '{n}'

export type MethodeEcritureMetier = 'POST' | 'PUT'

type FormeEcritureMetier = {
  readonly capacite: CapaciteEcriture
  readonly methode: MethodeEcritureMetier
  /** Hôte exact de l'API : jamais un sous-domaine, jamais un autre port. */
  readonly domaine: string
  /** Le chemin, segment par segment : un texte fixe, ou `NUMERO` ; jamais un préfixe. */
  readonly segments: readonly string[]
  /** Type de contenu de la requête (début de l'en-tête Content-Type, sans paramètre). */
  readonly typeContenu: string
  /**
   * Le dépôt relie aussi le document à l'opération de son chemin (Dougs) : le portail couvre les
   * deux capacités d'une seule requête, et aucune liaison à part n'existe.
   */
  readonly relieAussi?: true
}

/**
 * Une écriture métier : sa capacité, sa méthode, son hôte et son chemin, son type de contenu, et
 * son corps : libre (multipart, que l'interception ne peut pas relire) ou exact, construit depuis
 * le gabarit (un `NUMERO` par identifiant, rendu en nombre JSON) et comparé tel quel.
 */
export type EcritureMetier =
  | (FormeEcritureMetier & { readonly corps: 'libre' })
  | (FormeEcritureMetier & { readonly corps: 'exact'; readonly gabarit: string })

/**
 * Les seules écritures métier, par fournisseur. Tiime (visites du 11 et du 12/10/2026, notes hors
 * du dépôt) : déposer un fichier dans une catégorie de documents (multipart : `file` et
 * `source=web`), puis relier ce document à une transaction bancaire. La liaison remplace la liste
 * des documents de la transaction : elle ne vise qu'une transaction sans aucun document, relue
 * juste avant (l'adaptateur), et son corps n'a qu'un document. Dougs (dépôts réels du 14/10/2026,
 * forme relue le 31/10/2026, notes hors du dépôt) : une seule requête, sur l'opération (multipart :
 * `file`), qui rend l'opération avec sa pièce, relue juste avant dans la même fonction de page.
 */
export const ECRITURES_METIER: {
  readonly [K in FournisseurPortail]?: readonly EcritureMetier[]
} = {
  tiime: [
    {
      capacite: 'deposer_justificatif',
      methode: 'POST',
      domaine: 'chronos-api.tiime-apps.com',
      segments: ['v1', 'accounts', 'companies', NUMERO, 'document_categories', NUMERO, 'documents'],
      typeContenu: 'multipart/form-data',
      corps: 'libre',
    },
    {
      capacite: 'relier_justificatif',
      methode: 'PUT',
      domaine: 'chronos-api.tiime-apps.com',
      segments: [
        'v1',
        'accounts',
        'companies',
        NUMERO,
        'bank_transactions',
        NUMERO,
        'document_matchings',
      ],
      typeContenu: 'application/json',
      corps: 'exact',
      gabarit: '{"documents":[{"id":{n}}]}',
    },
  ],
  dougs: [
    {
      capacite: 'deposer_justificatif',
      relieAussi: true,
      methode: 'POST',
      domaine: 'app.dougs.fr',
      segments: [
        'companies',
        NUMERO,
        'operations',
        NUMERO,
        'source-document-attachments',
        'actions',
        'create-from-formdata',
      ],
      typeContenu: 'multipart/form-data',
      corps: 'libre',
    },
  ],
}

/** La table d'un fournisseur ; vide pour un autre ou un inconnu (jamais une clé héritée). */
export function ecrituresMetierDe(fournisseur: string): readonly EcritureMetier[] {
  return Object.hasOwn(ECRITURES_METIER, fournisseur)
    ? (ECRITURES_METIER[fournisseur as FournisseurPortail] ?? [])
    : []
}

/** Chemin lisible d'une écriture métier (« /v1/…/{n}/documents »), pour les messages. */
export function cheminEcritureMetier(e: EcritureMetier): string {
  return `/${e.segments.join('/')}`
}

/** Ce qu'un adaptateur déclare, lu par le navigateur avant toute requête. */
export type DeclarationPortail = {
  readonly cle: string
  readonly libelle: string
  readonly urlDepart: string
  /** Noms exacts ; une entrée qui commence par un point couvre ses sous-domaines. */
  readonly domaines: readonly string[]
  /** Écritures génériques (connexion d'un espace client) : jamais un chemin métier. */
  readonly ecritures: readonly EcriturePermise[]
  /**
   * Écritures métier d'un portail de justificatifs : exactement des entrées de la table de son
   * fournisseur (`ECRITURES_METIER`), chacune avec sa capacité vraie, sur son hôte (la boucle locale
   * hors production seulement). Jamais permises par la seule déclaration : une requête armée à la
   * fois (`consommerPermis`). Un passage à blanc les retire (`sansEcrituresMetier`).
   */
  readonly ecrituresMetier?: readonly EcritureMetier[]
  readonly capacites: Capacites
  /**
   * Chemins (débuts) des pages que le portail ne montre qu'une fois connecté, sur un domaine
   * déclaré : l'onglet rendu au titulaire n'est repris que là. À déclarer quand la connexion se
   * fait sous la page de départ (même hôte, même début de chemin) ; sans eux, l'onglet est repris
   * quand il revient sur la page de départ après l'avoir quittée. Le `#` compte : une application
   * d'une seule page y déclare ses routes (`/#/dashboard`).
   */
  readonly pagesConnecte?: readonly string[]
  /**
   * Page où l'onglet est rendu au titulaire pour qu'il se connecte, quand ce n'est pas la page de
   * départ (une page légère où rien de l'appli ne tourne, par exemple) : en https, sur un domaine
   * déclaré, vérifiée au chargement. Sans elle, l'onglet est rendu sur la page de départ.
   */
  readonly urlConnexion?: string
  /**
   * L'onglet rendu n'est repris que sur la page de départ, jamais sur la page de connecté atteinte,
   * après ce délai : ce que cette page ouvre (la session d'un autre site) doit se faire sans
   * pilotage ni règle, et le robot n'y retourne pas (Crédit Agricole).
   */
  readonly repriseSurDepart?: { readonly apresMs: number }
  /** Ce que le titulaire a à faire dans l'onglet rendu, quand se connecter ne suffit pas. */
  readonly consigneConnexion?: string
  /**
   * L'adaptateur ne tourne que dans l'onglet prêté du Chrome de tous les jours du titulaire
   * (extension reliée), jamais dans une fenêtre lancée par l'agent ni sur le serveur. Exigé d'un
   * portail de justificatifs (CLAUDE.md, « Dépôt et affectation des justificatifs »).
   */
  readonly exigeOngletPrete?: true
}

const METHODES_LECTURE = new Set(['GET', 'HEAD', 'OPTIONS'])
/** Un nom d'hôte exact (ni entrée en point, ni adresse, ni port). */
const HOTE_EXACT = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/
const LOCAUX = new Set(['127.0.0.1', 'localhost', '::1', '[::1]'])

function normaliserHote(hote: string): string {
  return hote.trim().toLowerCase().replace(/\.$/, '')
}

export function hoteAutorise(hote: string, domaines: readonly string[]): boolean {
  const h = normaliserHote(hote)
  if (!h) return false
  return domaines.some((d) => {
    const n = normaliserHote(d)
    if (n.startsWith('.')) return h === n.slice(1) || h.endsWith(n)
    return h === n
  })
}

/**
 * `ecriture_metier` : une écriture métier (de la déclaration ou de la table du fournisseur) qui
 * n'a pas été armée ; l'interception la tient pour une violation (collecte bloquée).
 */
export type DecisionRequete =
  | { permis: true }
  | {
      permis: false
      motif: 'domaine' | 'protocole' | 'ecriture' | 'adresse' | 'ecriture_metier'
    }

/** Chemin sans « . » ni « .. » : `new URL` les résout déjà, on refuse ce qui en reste encodé. */
function cheminCorrespond(chemin: string, motif: string): boolean {
  if (/%2e|%2f|%5c/i.test(chemin)) return false
  if (motif.endsWith('/*')) return chemin.startsWith(motif.slice(0, -1))
  return chemin === motif
}

/** Identifiant d'un segment `NUMERO` dans une requête : 1 à 15 chiffres. */
const SEGMENT_NUMERO = /^[0-9]{1,15}$/

/** Le chemin d'une requête (déjà résolu par `new URL`) a-t-il exactement ces segments ? */
function segmentsCorrespondent(chemin: string, segments: readonly string[]): boolean {
  if (/%2e|%2f|%5c/i.test(chemin)) return false
  const parts = chemin.split('/')
  if (parts.shift() !== '' || parts.length !== segments.length) return false
  return parts.every((p, i) =>
    segments[i] === NUMERO ? SEGMENT_NUMERO.test(p) : p === segments[i],
  )
}

/**
 * La requête est-elle cette écriture métier ? Même méthode ; https (http seulement sur la boucle
 * locale) ; hôte exact, sans autre port hors de la boucle locale ; ni identifiants, ni requête, ni
 * fragment ; chemin segment par segment, chaque `NUMERO` en 1 à 15 chiffres, aucun séparateur ni
 * point encodé (`%2e`, `%2f`, `%5c`).
 */
export function correspondEcritureMetier(
  e: EcritureMetier,
  r: { url: string; methode: string },
): boolean {
  if (r.methode.toUpperCase() !== e.methode || /[?#]/.test(r.url)) return false
  let u: URL
  try {
    u = new URL(r.url)
  } catch {
    return false
  }
  if (u.username !== '' || u.password !== '') return false
  const local = LOCAUX.has(u.hostname)
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && local)) return false
  if (u.port !== '' && !local) return false
  return u.hostname === e.domaine && segmentsCorrespondent(u.pathname, e.segments)
}

/** Les écritures métier en jeu pour une déclaration : les siennes, et la table de sa clé. */
function ecrituresMetierEnJeu(
  d: Partial<Pick<DeclarationPortail, 'cle' | 'ecrituresMetier'>>,
): readonly EcritureMetier[] {
  return [...(d.ecrituresMetier ?? []), ...(d.cle === undefined ? [] : ecrituresMetierDe(d.cle))]
}

/**
 * Une requête du navigateur : domaine déclaré, https (http seulement sur la boucle locale,
 * jamais déclarée en production), écriture seulement si l'adaptateur l'a déclarée. Une écriture
 * métier (déclarée, ou de la table du fournisseur : passage à blanc compris) n'est jamais permise
 * ici : seule une requête armée passe, une fois (`consommerPermis`, appelé avant).
 */
export function politiqueRequete(
  d: Pick<DeclarationPortail, 'domaines' | 'ecritures'> &
    Partial<Pick<DeclarationPortail, 'cle' | 'ecrituresMetier'>>,
  r: { url: string; methode: string },
): DecisionRequete {
  let url: URL
  try {
    url = new URL(r.url)
  } catch {
    return { permis: false, motif: 'adresse' }
  }
  if (url.protocol === 'data:' || url.protocol === 'blob:' || url.href === 'about:blank')
    return { permis: true }
  const local = LOCAUX.has(normaliserHote(url.hostname))
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local))
    return { permis: false, motif: 'protocole' }
  if (!hoteAutorise(url.hostname, d.domaines)) return { permis: false, motif: 'domaine' }
  const methode = r.methode.toUpperCase()
  if (METHODES_LECTURE.has(methode)) return { permis: true }
  if (ecrituresMetierEnJeu(d).some((e) => correspondEcritureMetier(e, r)))
    return { permis: false, motif: 'ecriture_metier' }
  const hote = normaliserHote(url.hostname)
  const ecrit = d.ecritures.some(
    (e) =>
      e.methode.toUpperCase() === methode &&
      (e.domaine === undefined || normaliserHote(e.domaine) === hote) &&
      cheminCorrespond(url.pathname, e.chemin),
  )
  return ecrit ? { permis: true } : { permis: false, motif: 'ecriture' }
}

/** Erreurs d'une déclaration ; vide si elle est acceptable. */
export function verifierDeclaration(
  d: DeclarationPortail,
  options: { production: boolean },
): string[] {
  const erreurs: string[] = []
  const capacites = d.capacites as Record<string, unknown>
  for (const [cle, valeur] of Object.entries(capacites)) {
    if ((CAPACITES_INTERDITES as readonly string[]).includes(cle)) {
      if (valeur !== false) erreurs.push(`capacité interdite : ${cle}`)
    } else if (
      !(CAPACITES_PERMISES as readonly string[]).includes(cle) &&
      !(CAPACITES_ECRITURE as readonly string[]).includes(cle)
    ) {
      erreurs.push(`capacité inconnue : ${cle}`)
    } else if (typeof valeur !== 'boolean') {
      erreurs.push(`capacité mal déclarée : ${cle}`)
    }
  }
  for (const c of CAPACITES_INTERDITES)
    if (!(c in capacites)) erreurs.push(`capacité interdite non déclarée : ${c}`)
  if (d.domaines.length === 0) erreurs.push('aucun domaine déclaré')
  for (const dom of d.domaines) {
    const n = normaliserHote(dom)
    if (!/^\.?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/.test(n)) {
      erreurs.push(`domaine illisible : ${dom}`)
      continue
    }
    if (n.startsWith('.') && n.split('.').filter(Boolean).length < 2)
      erreurs.push(`domaine trop large : ${dom}`)
    if (LOCAUX.has(n.replace(/^\./, '')) && options.production)
      erreurs.push(`boucle locale interdite en production : ${dom}`)
  }
  let depart: URL | null = null
  try {
    depart = new URL(d.urlDepart)
  } catch {
    erreurs.push('adresse de départ illisible')
  }
  if (depart && politiqueRequete(d, { url: d.urlDepart, methode: 'GET' }).permis !== true)
    erreurs.push('adresse de départ hors des domaines déclarés')
  if (d.urlConnexion !== undefined) {
    let connexion: URL | null = null
    try {
      connexion = new URL(d.urlConnexion)
    } catch {
      erreurs.push('adresse de connexion illisible')
    }
    // Une page d'un domaine déclaré : ni ressource interne (data:, blob:, about:), ni script.
    if (
      connexion &&
      (!['https:', 'http:'].includes(connexion.protocol) ||
        politiqueRequete(d, { url: d.urlConnexion, methode: 'GET' }).permis !== true)
    )
      erreurs.push('adresse de connexion hors des domaines déclarés')
  }
  if (modeDeDepot(d.cle) !== null && d.exigeOngletPrete !== true)
    erreurs.push('portail de justificatifs : onglet prêté exigé')
  for (const e of d.ecritures) {
    if (METHODES_LECTURE.has(e.methode.toUpperCase()))
      erreurs.push(`écriture déclarée avec une méthode de lecture : ${e.methode}`)
    if (!e.chemin.startsWith('/')) erreurs.push(`chemin d'écriture relatif : ${e.chemin}`)
    if (e.domaine === undefined) continue
    const ecriture = `${e.methode} ${e.chemin} (${e.domaine})`
    // Un hôte exact : jamais une entrée en point, une adresse ni un port.
    if (!HOTE_EXACT.test(normaliserHote(e.domaine)))
      erreurs.push(`domaine d’écriture illisible : ${ecriture}`)
    else if (!hoteAutorise(e.domaine, d.domaines))
      erreurs.push(`écriture sur un domaine non déclaré : ${ecriture}`)
  }
  for (const c of d.pagesConnecte ?? [])
    if (!c.startsWith('/')) erreurs.push(`page de connecté relative : ${c}`)
  erreurs.push(...erreursEcrituresMetier(d, options))
  return erreurs
}

/** Même écriture métier, à l'hôte près : capacité, méthode, chemin, type, corps et gabarit. */
function memeForme(a: EcritureMetier, b: EcritureMetier): boolean {
  return (
    a.capacite === b.capacite &&
    a.relieAussi === b.relieAussi &&
    a.methode === b.methode &&
    a.typeContenu === b.typeContenu &&
    a.segments.length === b.segments.length &&
    a.segments.every((s, i) => s === b.segments[i]) &&
    (a.corps === 'libre' ? b.corps === 'libre' : b.corps === 'exact' && a.gabarit === b.gabarit)
  )
}

/**
 * Une écriture générique (chemin exact, ou préfixe terminé par « /* ») peut-elle viser ce chemin
 * métier ? Chaque segment entier concorde (un `NUMERO` avec des chiffres) ; un préfixe peut
 * s'arrêter en chemin.
 */
function couvreCheminMetier(chemin: string, segments: readonly string[]): boolean {
  const prefixe = chemin.endsWith('/*')
  const parts = (prefixe ? chemin.slice(0, -1) : chemin).split('/')
  if (parts.shift() !== '') return false
  if (prefixe ? parts.length > segments.length : parts.length !== segments.length) return false
  return parts.every((p, i) => {
    const s = segments[i]!
    // Le dernier morceau d'un préfixe (vide : il finit par « / ») commence un segment.
    if (prefixe && i === parts.length - 1)
      return s === NUMERO ? /^[0-9]{0,15}$/.test(p) : s.startsWith(p)
    return s === NUMERO ? SEGMENT_NUMERO.test(p) : p === s
  })
}

/**
 * Les écritures métier d'une déclaration : exactement des entrées de la table de son fournisseur
 * (hôte compris, la boucle locale hors production seulement, sur un domaine déclaré), chacune avec
 * sa capacité vraie et chaque capacité vraie avec son écriture ; aucune capacité d'écriture hors de
 * cette table ; aucune écriture générique sur un chemin métier.
 */
function erreursEcrituresMetier(d: DeclarationPortail, options: { production: boolean }): string[] {
  const erreurs: string[] = []
  const capacites = d.capacites as Record<string, unknown>
  const table = ecrituresMetierDe(d.cle)
  const metier = d.ecrituresMetier ?? []
  for (const c of CAPACITES_ECRITURE) {
    if (capacites[c] !== true) continue
    if (!table.some((e) => e.capacite === c))
      erreurs.push(`capacité d'écriture hors de la table du fournisseur : ${c}`)
    else if (!metier.some((e) => e.capacite === c))
      erreurs.push(`capacité d'écriture sans son écriture : ${c}`)
  }
  for (const e of metier) {
    const modele = table.find((t) => memeForme(t, e))
    if (!modele) {
      erreurs.push(
        `écriture métier hors de la table du fournisseur : ${e.methode} ${cheminEcritureMetier(e)}`,
      )
      continue
    }
    if (capacites[e.capacite] !== true)
      erreurs.push(`écriture métier sans sa capacité : ${e.capacite}`)
    const local = LOCAUX.has(normaliserHote(e.domaine))
    if (e.domaine !== modele.domaine && !(local && !options.production))
      erreurs.push(`écriture métier sur un autre domaine que celui de la table : ${e.domaine}`)
    if (!hoteAutorise(e.domaine, d.domaines))
      erreurs.push(`écriture métier sur un domaine non déclaré : ${e.domaine}`)
  }
  const chemins = [...table, ...metier].map((e) => e.segments)
  for (const g of d.ecritures)
    if (chemins.some((s) => couvreCheminMetier(g.chemin, s)))
      erreurs.push(`écriture générique sur un chemin métier : ${g.methode} ${g.chemin}`)
  return erreurs
}

/**
 * La déclaration effective d'un passage à blanc : aucune écriture métier, capacités d'écriture
 * fausses ; le reste identique. Une écriture de la table y reste reconnue (et refusée) par
 * `politiqueRequete`, par la clé du fournisseur.
 */
export function sansEcrituresMetier(d: DeclarationPortail): DeclarationPortail {
  const capacites: Record<string, unknown> = { ...d.capacites }
  for (const c of CAPACITES_ECRITURE) if (Object.hasOwn(capacites, c)) capacites[c] = false
  return { ...d, ecrituresMetier: [], capacites: capacites as Capacites }
}

// Requêtes armées : la seule voie d'une écriture métier

/** Identifiant d'une requête armée : un entier écrit sans zéro de tête, 15 chiffres au plus. */
const IDENTIFIANT_ARME = /^[1-9][0-9]{0,14}$/

const echapperRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * L'origine (« https://hôte », sans chemin) est-elle celle de l'écriture ? Son hôte exact, en https
 * sans autre port ; sur la boucle locale (essais), http ou https et tout port.
 */
function origineDe(e: EcritureMetier, origine: string): URL | null {
  let o: URL
  try {
    o = new URL(origine)
  } catch {
    return null
  }
  if (o.origin !== origine || o.hostname !== e.domaine) return null
  const local = LOCAUX.has(o.hostname)
  if (o.protocol !== 'https:' && !(o.protocol === 'http:' && local)) return null
  return o.port === '' || local ? o : null
}

/** Ce que le moteur s'apprête à envoyer : une écriture métier déclarée, exactement. */
export type RequeteArmee = {
  readonly capacite: CapaciteEcriture
  readonly methode: MethodeEcritureMetier
  /** Adresse complète : origine, chemin, ni requête ni fragment. */
  readonly url: string
  readonly typeContenu: string
  /** Corps exact attendu ; null : corps libre (multipart), non relu à l'interception. */
  readonly corps: string | null
}

/**
 * La requête armée d'une écriture métier : l'origine de son hôte, un identifiant par `NUMERO` du
 * chemin, et pour un corps exact un identifiant par `NUMERO` du gabarit (rendu en nombre JSON, sans
 * espace). Chaque identifiant est un entier de 1 à 15 chiffres sans zéro de tête. Rien d'autre ne
 * s'arme : un identifiant illisible, un de trop ou de moins, une autre origine donnent null.
 */
export function requeteArmee(
  e: EcritureMetier,
  o: { origine: string; chemin: readonly string[]; corps?: readonly string[] },
): RequeteArmee | null {
  const origine = origineDe(e, o.origine)
  if (!origine) return null
  const lisibles = (ids: readonly string[]) => ids.every((x) => IDENTIFIANT_ARME.test(x))
  const trous = e.segments.filter((s) => s === NUMERO).length
  if (o.chemin.length !== trous || !lisibles(o.chemin)) return null
  let i = 0
  const chemin = e.segments.map((s) => (s === NUMERO ? o.chemin[i++]! : s)).join('/')
  let corps: string | null = null
  if (e.corps === 'exact') {
    const morceaux = e.gabarit.split(NUMERO)
    const valeurs = o.corps ?? []
    if (valeurs.length !== morceaux.length - 1 || !lisibles(valeurs)) return null
    corps = morceaux.reduce((acc, m, j) => acc + valeurs[j - 1]! + m)
  } else if ((o.corps ?? []).length > 0) return null
  return {
    capacite: e.capacite,
    methode: e.methode,
    url: `${origine.origin}/${chemin}`,
    typeContenu: e.typeContenu,
    corps,
  }
}

/** Le corps est-il le gabarit, chaque `NUMERO` rempli d'un identifiant armé ? Rien d'autre. */
function corpsSelonGabarit(gabarit: string, corps: string): boolean {
  const motif = gabarit.split(NUMERO).map(echapperRegex).join('[1-9][0-9]{0,14}')
  return new RegExp(`^${motif}$`).test(corps)
}

/** Un permis ne vaut que 90 secondes après avoir été armé. */
export const DUREE_PERMIS_ECRITURE_MS = 90_000

/** Une requête armée, en attente de sa requête exacte jusqu'à `expireLe` (millisecondes). */
export type PermisEcriture = RequeteArmee & { readonly expireLe: number }

/**
 * Le permis d'une requête armée, seulement si elle est exactement une écriture métier de la
 * déclaration effective (même capacité, méthode, adresse, type de contenu, corps exact ou libre),
 * sur un domaine déclaré ; une déclaration à blanc (`sansEcrituresMetier`) n'arme rien : null.
 */
export function permisEcriture(
  d: Pick<DeclarationPortail, 'domaines' | 'ecrituresMetier'>,
  r: RequeteArmee,
  maintenantMs: number,
): PermisEcriture | null {
  let hote: string
  try {
    hote = new URL(r.url).hostname
  } catch {
    return null
  }
  const declaree = (d.ecrituresMetier ?? []).some(
    (e) =>
      e.capacite === r.capacite &&
      e.typeContenu === r.typeContenu &&
      (e.corps === 'exact'
        ? r.corps !== null && corpsSelonGabarit(e.gabarit, r.corps)
        : r.corps === null) &&
      correspondEcritureMetier(e, { url: r.url, methode: r.methode }),
  )
  if (!declaree || !hoteAutorise(hote, d.domaines)) return null
  return { ...r, expireLe: maintenantMs + DUREE_PERMIS_ECRITURE_MS }
}

/** Une requête vue par l'interception : son type (en-tête Content-Type) et son corps, lus. */
export type RequeteInterceptee = {
  methode: string
  url: string
  typeContenu: string | null
  corps: string | null
}

/** Le type de contenu d'un en-tête, sans ses paramètres (« multipart/form-data; boundary=… »). */
const typeSansParametre = (t: string | null) => t?.split(';')[0]?.trim().toLowerCase() ?? null

/**
 * Le permis qu'une requête consomme, s'il en est un : même méthode, même adresse complète (au signe
 * près), même type de contenu (paramètres ignorés), même corps quand il est armé exact ; jamais un
 * permis échu (retiré). Usage unique : le permis consommé sort des restants. Une OPTIONS (pré-vol
 * du navigateur) ne consomme jamais rien.
 */
export function consommerPermis(
  permis: readonly PermisEcriture[],
  r: RequeteInterceptee,
  maintenantMs: number,
): { permise: boolean; restants: PermisEcriture[] } {
  const restants = permis.filter((p) => p.expireLe > maintenantMs)
  const methode = r.methode.toUpperCase()
  if (methode === 'OPTIONS') return { permise: false, restants }
  const i = restants.findIndex(
    (p) =>
      p.methode === methode &&
      p.url === r.url &&
      typeSansParametre(r.typeContenu) === p.typeContenu &&
      (p.corps === null || p.corps === r.corps),
  )
  if (i < 0) return { permise: false, restants }
  return { permise: true, restants: restants.filter((_, j) => j !== i) }
}

/**
 * L'expression (RE2, sans anticipation) d'une écriture métier sur son origine, pour la règle
 * `allow` de l'extension : l'adresse entière, ancrée, chaque `NUMERO` en 1 à 15 chiffres, ni
 * requête ni fragment. Sensible à la casse (`isUrlFilterCaseSensitive` côté extension). Une autre
 * origine que celle de l'écriture : null.
 */
export function regexEcritureMetier(e: EcritureMetier, origine: string): string | null {
  const o = origineDe(e, origine)
  if (!o) return null
  const chemin = e.segments.map((s) => (s === NUMERO ? '[0-9]{1,15}' : echapperRegex(s))).join('/')
  return `^${echapperRegex(o.origin)}/${chemin}$`
}

// Machine d'états de la collecte

export type EvenementCollecte = {
  type:
    | 'demarrer'
    | 'defi'
    | 'defi_resolu'
    | 'defi_expire'
    | 'terminer'
    | 'echouer'
    | 'bloquer'
    | 'interrompre'
    | 'annuler'
}

export type EffetCollecte =
  | 'notifier_titulaire'
  | 'demander_verification'
  | 'noter_succes'
  | 'noter_echec'
  | 'bloquer_connexion'
  | 'noter_interruption'

type LigneCollecte = {
  evenement: EvenementCollecte['type']
  depuis: readonly StatutCollecte[]
  vers: StatutCollecte
  effets: readonly EffetCollecte[]
}

const ACTIVES: readonly StatutCollecte[] = ['en_cours', 'reprise']

export const TRANSITIONS_COLLECTE: readonly LigneCollecte[] = [
  { evenement: 'demarrer', depuis: ['en_file'], vers: 'en_cours', effets: [] },
  { evenement: 'defi', depuis: ACTIVES, vers: 'attente_humain', effets: ['notifier_titulaire'] },
  { evenement: 'defi_resolu', depuis: ['attente_humain'], vers: 'reprise', effets: [] },
  {
    evenement: 'defi_expire',
    depuis: ['attente_humain'],
    vers: 'expiree',
    effets: ['demander_verification'],
  },
  { evenement: 'terminer', depuis: ACTIVES, vers: 'terminee', effets: ['noter_succes'] },
  {
    evenement: 'echouer',
    depuis: [...ACTIVES, 'attente_humain'],
    vers: 'echec',
    effets: ['noter_echec'],
  },
  {
    evenement: 'bloquer',
    depuis: [...ACTIVES, 'attente_humain'],
    vers: 'bloquee',
    effets: ['bloquer_connexion'],
  },
  /** Collecteur arrêté en pleine collecte : on la clôt, la suivante se reconnectera. */
  {
    evenement: 'interrompre',
    depuis: [...ACTIVES, 'attente_humain'],
    vers: 'echec',
    effets: ['noter_interruption'],
  },
  /** Collecte en file, ou à reprendre, retirée (connexion désactivée, collecte web coupée). */
  { evenement: 'annuler', depuis: ['en_file', 'reprise'], vers: 'annulee', effets: [] },
]

export class TransitionCollecteInvalide extends Error {
  constructor(statut: string, evenement: string) {
    super(`Transition de collecte impossible : « ${evenement} » depuis « ${statut} ».`)
  }
}

export function transitionCollecte(
  etat: { statut: StatutCollecte },
  evenement: EvenementCollecte,
): { statut: StatutCollecte; effets: EffetCollecte[] } {
  const ligne = TRANSITIONS_COLLECTE.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionCollecteInvalide(etat.statut, evenement.type)
  return { statut: ligne.vers, effets: [...ligne.effets] }
}

export const STATUTS_COLLECTE_OUVERTS: readonly StatutCollecte[] = [
  'en_file',
  'en_cours',
  'attente_humain',
  'reprise',
]

// Défis : ce que le fournisseur demande à un humain

export type EvenementDefi = { type: 'resoudre' | 'expirer' | 'annuler' }

export const TRANSITIONS_DEFI: readonly {
  evenement: EvenementDefi['type']
  depuis: readonly StatutDefi[]
  vers: StatutDefi
}[] = [
  { evenement: 'resoudre', depuis: ['en_attente'], vers: 'resolu' },
  { evenement: 'expirer', depuis: ['en_attente'], vers: 'expire' },
  { evenement: 'annuler', depuis: ['en_attente'], vers: 'annule' },
]

export function transitionDefi(
  etat: { statut: StatutDefi },
  evenement: EvenementDefi,
): { statut: StatutDefi } {
  const ligne = TRANSITIONS_DEFI.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new Error(`Défi : « ${evenement.type} » impossible depuis « ${etat.statut} ».`)
  return { statut: ligne.vers }
}

/**
 * Ce que l'appli peut faire d'un défi. Un code se saisit dans l'appli et repart, chiffré et à
 * usage unique, vers le navigateur qui attend ; une validation dans l'appli du fournisseur se
 * guette sans rien saisir ; un CAPTCHA, une connexion à faire soi-même, des conditions à accepter
 * ou une étape inconnue, l'humain les franchit lui-même dans la vue en direct du navigateur du
 * collecteur (décision du 02/10/2026, coupe-circuit `portails.vue_en_direct`) ; le reste (clé
 * d'accès, clé de sécurité : l'authentificateur n'est pas sur le serveur ; mot de passe à changer :
 * l'humain le fait chez lui puis met à jour Bitwarden) ne se finit pas sur le serveur : la
 * collecte est bloquée.
 */
export type ConduiteDefi =
  /** Sur l'ordinateur du titulaire : il fait l'étape lui-même dans la fenêtre du collecteur local. */
  | { mode: 'local'; minutes: number }
  | { mode: 'code'; minutes: number }
  | { mode: 'attente'; minutes: number }
  | { mode: 'vue'; minutes: number }
  | { mode: 'impossible' }

/** Défis qu'un humain franchit lui-même, depuis la vue en direct. */
export const DEFIS_VUE: readonly TypeDefi[] = [
  'captcha',
  'connexion_interactive',
  'consentement',
  'inconnu',
]

/**
 * `vueEnDirect` : coupe-circuit `portails.vue_en_direct` ; faux (défaut), la vue retombe sur
 * « impossible ». `lieu` : `local` quand la collecte tourne sur l'ordinateur du titulaire
 * (docs/collecteur-local.md) : il voit la fenêtre et y fait lui-même l'étape (code, validation,
 * CAPTCHA, clé d'accès, conditions), rien ne transite par l'appli ; seul un mot de passe à changer
 * reste impossible (écriture sur le compte, à faire dans son navigateur habituel).
 */
export function conduiteDefi(
  type: TypeDefi,
  options: { vueEnDirect?: boolean; lieu?: 'serveur' | 'local' } = {},
): ConduiteDefi {
  if (options.lieu === 'local')
    return type === 'changement_mot_de_passe'
      ? { mode: 'impossible' }
      : { mode: 'local', minutes: 10 }
  switch (type) {
    case 'otp_sms':
    case 'otp_email':
    case 'totp':
      return { mode: 'code', minutes: 10 }
    case 'push':
      return { mode: 'attente', minutes: 5 }
    default:
      return DEFIS_VUE.includes(type) && options.vueEnDirect
        ? { mode: 'vue', minutes: 10 }
        : { mode: 'impossible' }
  }
}

/** Ce que voit l'humain, dans l'appli et dans la notification. */
export const LIBELLES_DEFI: Record<TypeDefi, string> = {
  otp_sms: 'code reçu par SMS',
  otp_email: 'code reçu par mail',
  totp: 'code de l’application d’authentification',
  push: 'validation dans l’application du fournisseur',
  passkey: 'clé d’accès (passkey)',
  webauthn: 'clé de sécurité',
  connexion_interactive: 'connexion à faire soi-même',
  captcha: 'CAPTCHA',
  changement_mot_de_passe: 'mot de passe à changer',
  consentement: 'conditions à accepter',
  inconnu: 'vérification inconnue',
}

/** Code saisi par l'humain : 4 à 12 lettres ou chiffres, espaces retirés ; sinon null. */
export function codeDefiValide(saisie: string): string | null {
  const code = saisie.replace(/[\s-]/g, '')
  return /^[0-9A-Za-z]{4,12}$/.test(code) ? code : null
}

/**
 * Sans humain (collecte planifiée, la nuit), ne pas soumettre le mot de passe d'un compte dont
 * la dernière connexion a demandé un code ou une validation, ni d'un compte jamais connecté (on ne
 * sait pas ce qu'il demandera) : le fournisseur enverrait un code à une heure où personne ne
 * répond, et des envois répétés peuvent verrouiller le compte. La connexion attend que son
 * titulaire dise être là (demande de disponibilité). Un TOTP connu du coffre se calcule seul.
 */
export function peutAuthentifierSansHumain(e: {
  declencheur: DeclencheurCollecte
  dernierDefi: TypeDefi | null
  /** La connexion a déjà réussi au moins une fois. */
  dejaConnectee: boolean
}): boolean {
  if (e.declencheur === 'manuel') return true
  if (e.dernierDefi === null) return e.dejaConnectee
  return e.dernierDefi === 'totp'
}

// Planification

export const HEURE_COLLECTE = '02:00'
/** Délais entre les essais après un échec ; au-delà, la connexion est bloquée. */
export const DELAIS_ESSAI_MINUTES = [60, 240, 1440, 1440] as const
export const MAX_ECHECS_COLLECTE = DELAIS_ESSAI_MINUTES.length + 1

/** Prochaine collecte planifiée : dans `frequenceJours` jours, à 2 heures à Paris. */
export function prochaineCollecte(apres: Date, frequenceJours: number): Date {
  const jour = ajouterJours(jourAParis(apres), Math.max(1, frequenceJours))
  return fromZonedTime(`${jour}T${HEURE_COLLECTE}:00`, FUSEAU)
}

/** Une collecte qui a laissé des documents au-delà de sa limite : la suite vient vite. */
export const MINUTES_AVANT_SUITE = 15

/**
 * Prochaine collecte après un succès : dans un quart d'heure s'il reste des documents au-delà
 * de la limite d'une collecte (`restants`), sinon à la fréquence de la connexion.
 */
export function collecteSuivante(maintenant: Date, frequenceJours: number, restants = 0): Date {
  return restants > 0
    ? new Date(maintenant.getTime() + MINUTES_AVANT_SUITE * 60_000)
    : prochaineCollecte(maintenant, frequenceJours)
}

/** Après le n-ième échec d'affilée : quand réessayer, ou null (bloquée). */
export function prochainEssai(echecs: number, maintenant: Date): Date | null {
  const delai = DELAIS_ESSAI_MINUTES[echecs - 1]
  if (echecs >= MAX_ECHECS_COLLECTE || delai === undefined) return null
  return new Date(maintenant.getTime() + delai * 60_000)
}

export function etatConnexionApres(
  statut: StatutCollecte,
  echecsConsecutifs: number,
): EtatConnexionPortail {
  switch (statut) {
    case 'terminee':
      return 'ok'
    case 'bloquee':
      return 'bloquee'
    case 'expiree':
    case 'attente_humain':
      return 'verification_requise'
    case 'echec':
      return echecsConsecutifs >= MAX_ECHECS_COLLECTE ? 'bloquee' : 'erreur'
    default:
      return 'erreur'
  }
}

// Caviardage : aucune valeur sensible dans un message, un journal ou une erreur

const MASQUE = '[masqué]'
const LONGUEUR_MAX = 500

const MOTIFS_SENSIBLES: readonly [RegExp, string][] = [
  [/\b(authorization|proxy-authorization|set-cookie|cookie)(\s*[:=]\s*)[^\n]+/gi, `$1$2${MASQUE}`],
  [/\bbearer\s+[\w\-.~+/]+=*/gi, `Bearer ${MASQUE}`],
  [/\b(mot de passe|code secret|code de vérification)(\s*[:=]\s*)\S+/gi, `$1$2${MASQUE}`],
  [/\beyJ[\w-]{4,}\.[\w-]{4,}\.[\w-]{4,}/g, MASQUE],
  [
    /\b([\w-]*(?:password|passwd|pwd|mot_de_passe|motdepasse|secret|token|jeton|otp|code|session|sid|cle|key)[\w-]*)(\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s&;,]+)/gi,
    `$1$2${MASQUE}`,
  ],
]

/**
 * Remplace chaque valeur sensible connue (mot de passe, code, valeurs de cookie), sous sa forme
 * brute ou encodée dans une adresse, puis les motifs reconnus sans les connaître (en-têtes,
 * jetons, paramètres nommés comme un secret). Borné : un message d'erreur reste court.
 */
export function caviarder(texte: string, sensibles: readonly string[]): string {
  let t = texte
  const valeurs = new Set<string>()
  for (const s of sensibles) {
    if (s.length < 3) continue
    valeurs.add(s)
    valeurs.add(encodeURIComponent(s))
    valeurs.add(encodeURIComponent(s).replace(/%20/g, '+'))
  }
  for (const v of [...valeurs].sort((a, b) => b.length - a.length)) t = t.split(v).join(MASQUE)
  for (const [motif, remplacement] of MOTIFS_SENSIBLES) t = t.replace(motif, remplacement)
  return t.length > LONGUEUR_MAX ? `${t.slice(0, LONGUEUR_MAX - 1)}…` : t
}

// Documents téléchargés : entrée non fiable

export const TAILLE_MAX_DOCUMENT = 25 * 1024 * 1024

export const TYPES_DOCUMENT = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'application/xml': 'xml',
} as const
export type TypeDocument = keyof typeof TYPES_DOCUMENT

function commencePar(octets: Uint8Array, signature: readonly number[], decalage = 0): boolean {
  return signature.every((o, i) => octets[decalage + i] === o)
}

/** Type d'après les premiers octets ; jamais d'après le nom ni l'en-tête du portail. */
export function typeDuContenu(debut: Uint8Array): TypeDocument | null {
  if (commencePar(debut, [0x25, 0x50, 0x44, 0x46, 0x2d])) return 'application/pdf'
  if (commencePar(debut, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png'
  if (commencePar(debut, [0xff, 0xd8, 0xff])) return 'image/jpeg'
  const bom = commencePar(debut, [0xef, 0xbb, 0xbf]) ? 3 : 0
  const texte = new TextDecoder('utf-8', { fatal: false })
    .decode(debut.subarray(bom, bom + 64))
    .trimStart()
  if (texte.startsWith('<?xml')) return 'application/xml'
  return null
}

/** Nom sûr : dernier segment, sans caractère de contrôle ni séparateur, extension du type réel. */
export function nomDocumentSur(propose: string, type: TypeDocument): string {
  const ext = TYPES_DOCUMENT[type]
  const segment = propose.split(/[/\\]/).pop() ?? ''
  const nettoye = segment
    .normalize('NFC')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f<>:"|?*]/g, '')
    .replace(/^\.+/, '')
    .trim()
  const actuelle = /\.([A-Za-z0-9]{1,5})$/.exec(nettoye)?.[1]?.toLowerCase()
  const garder = actuelle === ext || (ext === 'jpg' && actuelle === 'jpeg')
  const base = garder
    ? nettoye.slice(0, -(actuelle.length + 1))
    : nettoye.replace(/\.(pdf|png|jpe?g|xml|exe|html?|js|bat|cmd|com|scr|sh)$/i, '')
  const suffixe = `.${garder ? actuelle : ext}`
  const coupe = base.slice(0, 150 - suffixe.length).replace(/[.\s]+$/, '')
  return `${coupe || 'document'}${suffixe}`
}

export type ValidationDocument =
  { ok: true; type: TypeDocument; nom: string } | { ok: false; motif: string }

export function validerDocument(f: {
  debut: Uint8Array
  taille: number
  nomPropose: string
}): ValidationDocument {
  if (f.taille <= 0) return { ok: false, motif: 'fichier vide' }
  if (f.taille > TAILLE_MAX_DOCUMENT) return { ok: false, motif: 'fichier trop gros' }
  const type = typeDuContenu(f.debut)
  if (!type) return { ok: false, motif: 'type de fichier refusé (PDF, PNG, JPEG ou XML attendu)' }
  return { ok: true, type, nom: nomDocumentSur(f.nomPropose, type) }
}
